import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import OrderPeriodPicker from "../../components/OrderPeriodPicker";
import toast from "react-hot-toast";
import { getFornitoreCompleto } from "../../Admin/adminApi";
import {
  listSupplierOrders,
  updateSupplierOrderStatus,
} from "../../api/ordersApi";
import "../css/FornitoreOrdiniSupplier.css";

/* ---------- utils ---------- */
function formatCurrency(v) {
  const n = Number(v);
  const isCents = Number.isInteger(n) && n >= 50;
  const eur = isCents ? n / 100 : n;
  return eur.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function safeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateShort(iso) {
  const d = safeDate(iso);
  if (!d) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function formatDayMonth(iso) {
  const d = safeDate(iso);
  if (!d) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

function formatDayMonthWithSlot(dateIso, slot) {
  const base = formatDayMonth(dateIso);
  if (!dateIso) return "—";
  if (!slot || slot === "—") return base;
  return `${base} · ${slot}`;
}

function formatCity(citta, provincia) {
  const city = String(citta || "").trim();
  const prov = String(provincia || "").trim();
  if (city && prov) return `${city} (${prov})`;
  return city || prov || "—";
}

function getRoleFromToken() {
  try {
    const t = localStorage.getItem("token");
    if (!t) return null;
    const base64 = t.split(".")[1];
    const json = JSON.parse(atob(base64.replace(/-/g, "+").replace(/_/g, "/")));
    return String(json?.role || json?.user?.role || "").toLowerCase() || null;
  } catch {
    return null;
  }
}

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "pag", label: "Pagato" },
  { value: "ritir", label: "Ritirato" },
  { value: "incons", label: "Da Consegnare" },
  { value: "consegn", label: "Consegnato" },
  { value: "not_consegn", label: "<> Consegnato" },
];

const DB_STATUS_OPTIONS = [
  { value: "pagata", label: "Pagato" },
  { value: "ritirata", label: "Ritirato" },
  { value: "completata", label: "Completato" },
  { value: "in_consegna", label: "Da Consegnare" },
  { value: "consegnata", label: "Consegnato" },
];

const FILTER_TO_DB_STATUS = {
  pag: ["pagata"],
  ritir: ["ritirata"],
  incons: ["completata", "in_consegna"],
  consegn: ["consegnata"],
};

function statusMatches(rawStatus, filterKey) {
  if (!filterKey || filterKey === "all") return true;
  if (filterKey === "not_consegn") return rawStatus !== "consegnata";
  const targets = FILTER_TO_DB_STATUS[filterKey];
  if (!targets) return true;
  return targets.includes(rawStatus);
}

function humanize(raw) {
  switch (raw) {
    case "in_attesa":
      return "In attesa";
    case "accettata":
      return "Accettata";
    case "pagata":
      return "Pagata";
    case "ritirata":
      return "Ritirata";
    case "completata":
      return "Completata";
    case "in_consegna":
      return "In consegna";
    case "consegnata":
      return "Consegnato";
    case "annullata":
      return "Annullata";
    case "rifiutata":
      return "Rifiutata";
    default:
      return "In lavorazione";
  }
}

export default function FornitoreOrdini() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const fornitore = searchParams.get("fornitore") || "";
  const [supplierName, setSupplierName] = useState("");

  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [from, setFrom] = useState(searchParams.get("from") || "");
  const [to, setTo] = useState(searchParams.get("to") || "");

  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
  });

  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const [sort, setSort] = useState({ field: "date", dir: "desc" });

  useEffect(() => {
    if (!isAdmin || !fornitore) return;
    (async () => {
      try {
        const tkn = localStorage.getItem("token");
        const full = await getFornitoreCompleto(fornitore, tkn);
        const name =
          full?.name ??
          full?.ragione_sociale ??
          full?.nome ??
          full?.alias ??
          "";
        setSupplierName(name || `Fornitore #${fornitore}`);
      } catch {
        setSupplierName(`Fornitore #${fornitore}`);
      }
    })();
  }, [isAdmin, fornitore]);

  useEffect(() => {
    const qp = new URLSearchParams();
    qp.set("page", String(page));
    if (filters.status && filters.status !== "all")
      qp.set("status", filters.status);
    if (filters.from) qp.set("from", filters.from);
    if (filters.to) qp.set("to", filters.to);
    if (fornitore) qp.set("fornitore", fornitore);
    setSearchParams(qp, { replace: true });
  }, [page, filters, setSearchParams, fornitore]);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    listSupplierOrders({ page, pageSize: PAGE_SIZE, filters, fornitore })
      .then((res) => {
        if (!alive) return;
        setOrders(Array.isArray(res.data) ? res.data : []);
        setMeta({
          page: Number(res.page || page),
          totalPages: Number(res.totalPages || 1),
        });
      })
      .catch((err) => {
        console.error(
          "[FornitoreOrdini] listSupplierOrders error:",
          err?.message || err,
        );
        toast.error("Impossibile caricare gli ordini");
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [page, filters, refreshKey, fornitore]);

  const rows = useMemo(() => orders || [], [orders]);

  const automaticFilters = ["fornitore", "admin"].includes(getRoleFromToken());
  const invalidRange = !!(from && to && from > to);

  function commitFilters(next) {
    if (next.from && next.to && next.from > next.to) return;
    if (next.status === filters.status && next.from === filters.from && next.to === filters.to) return;
    setPage(1);
    setFilters(next);
  }

  function applyPeriod(range) {
    setFrom(range.from);
    setTo(range.to);
    commitFilters({ status, ...range });
  }

  function applyFilters() {
    setPage(1);
    setFilters({ status, from, to });
  }

  function resetFilters() {
    setStatus("all");
    setFrom("");
    setTo("");
    setPage(1);
    setFilters(prev => prev.status === "all" && !prev.from && !prev.to ? prev : { status: "all", from: "", to: "" });
  }

  function toggleSort(field) {
    setSort((prev) => {
      if (prev.field === field)
        return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { field, dir: "asc" };
    });
  }

  function sortArrow(field) {
    if (sort.field !== field) return "↕";
    return sort.dir === "asc" ? "↑" : "↓";
  }

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { field, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;

    const getDate = (o) =>
      new Date(o.created_at || o.createdAt || 0).getTime() || 0;
    const getStr = (v) => String(v || "").toLowerCase();
    const getNum = (v) => Number(v ?? 0) || 0;

    arr.sort((a, b) => {
      let va, vb;

      switch (field) {
        case "id":
          va = getNum(a.id);
          vb = getNum(b.id);
          return (va - vb) * mult;
        case "cliente":
          va = getStr(a.cliente);
          vb = getStr(b.cliente);
          return va.localeCompare(vb) * mult;
        case "citta":
          va = getStr(`${a.citta || ""} ${a.provincia || ""}`);
          vb = getStr(`${b.citta || ""} ${b.provincia || ""}`);
          return va.localeCompare(vb) * mult;
        case "total":
          va = getNum(a.total_cents);
          vb = getNum(b.total_cents);
          return (va - vb) * mult;
        case "merch":
          va = getNum(a.merch_total_cents);
          vb = getNum(b.merch_total_cents);
          return (va - vb) * mult;
        case "rosa":
          va = getNum(a.rosa_total_cents);
          vb = getNum(b.rosa_total_cents);
          return (va - vb) * mult;
        case "fee":
          va = getNum(a.logistics_fee);
          vb = getNum(b.logistics_fee);
          return (va - vb) * mult;
        case "sconto":
          va = getNum(a.sconto_cents);
          vb = getNum(b.sconto_cents);
          return (va - vb) * mult;
        case "status":
          va = getStr(a.status || a.raw_status);
          vb = getStr(b.status || b.raw_status);
          return va.localeCompare(vb) * mult;
        case "date":
        default:
          va = getDate(a);
          vb = getDate(b);
          return (va - vb) * mult;
      }
    });

    return arr;
  }, [rows, sort]);

  async function changeRowStatus(order, next) {
    const prev = order.raw_status;

    setOrders((list) =>
      list.map((o) =>
        o.id === order.id
          ? { ...o, raw_status: next, status: humanize(next) }
          : o,
      ),
    );
    setUpdating((u) => ({ ...u, [order.id]: true }));

    try {
      await updateSupplierOrderStatus(order.id, next, { fornitore });

      if (!statusMatches(next, filters.status)) {
        setOrders((prevList) => {
          const wasSingle = prevList.length === 1;
          const newList = prevList.filter((o) => o.id !== order.id);
          if (wasSingle && page > 1) setPage(page - 1);
          else setRefreshKey((k) => k + 1);
          return newList;
        });
        toast.success("Stato aggiornato · filtro applicato");
      } else {
        toast.success("Stato aggiornato");
      }
    } catch (err) {
      setOrders((list) =>
        list.map((o) =>
          o.id === order.id
            ? { ...o, raw_status: prev, status: humanize(prev) }
            : o,
        ),
      );
      toast.error(err?.message || "Errore aggiornamento stato");
    } finally {
      setUpdating((u) => {
        const { [order.id]: _drop, ...rest } = u;
        return rest;
      });
    }
  }

  return (
    <div className="supord-page">
      <div className="supord-inner">
        <header className="supord-header">
          <div className="supord-header-left">
            {isAdmin && (
              <button
                className="supord-btn supord-btn-ghost"
                onClick={() => navigate(-1)}
                aria-label="Indietro"
                title="Indietro"
              >
                ←
              </button>
            )}
            <div>
              <h1>
                {fornitore
                  ? `Ordini di ${supplierName || "…"}`
                  : "Ordini fornitore"}
              </h1>
              {fornitore && (
                <p className="supord-sub">
                  Vista fornitore · filtrabile per stato e data
                </p>
              )}
            </div>
          </div>
        </header>

        <section className={`supord-filters ${automaticFilters ? "supord-filters--range" : ""}`}>
          <div className="supord-filter-group">
            <label className="supord-label">Stato ordine</label>
            <select
              className="supord-input supord-select"
              value={status}
              onChange={(e) => { setStatus(e.target.value); if (automaticFilters) commitFilters({ status: e.target.value, ...(invalidRange ? { from: filters.from, to: filters.to } : { from, to }) }); }}
              id="supplier-status"
              aria-label="Stato ordine"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {automaticFilters ? <div className="supord-filter-group">
            <span className="supord-label">Periodo ordini</span>
            <OrderPeriodPicker from={filters.from} to={filters.to} onApply={applyPeriod} />
          </div> : <>
          <div className="supord-filter-group">
            <label className="supord-label">Dal</label>
            <input
              className="supord-input"
              type="date"
              value={from}
              aria-label="Dal"
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="supord-filter-group">
            <label className="supord-label">Al</label>
            <input
              className="supord-input"
              type="date"
              value={to}
              aria-label="Al"
              onChange={(e) => setTo(e.target.value)}
            />
          </div>

          </>}

          <div className="supord-filter-actions">
            <button
              className="supord-btn supord-btn-ghost"
              onClick={resetFilters}
              data-filter-reset
            >
              Pulisci
            </button>
            {!automaticFilters && <button
              className="supord-btn supord-btn-primary"
              onClick={applyFilters}
            >
              Filtra
            </button>}
            {automaticFilters && invalidRange && <small role="alert">La data Dal deve precedere o coincidere con la data Al.</small>}
          </div>
        </section>

        <section className="supord-table-section">
          <div className="supord-table-wrap">
            <table
              className="supord-table"
              aria-label="Elenco ordini fornitore"
            >
              <thead>
                <tr>
                  <th className="supord-col-dateord">
                    <button
                      type="button"
                      className="supord-th-btn"
                      onClick={() => toggleSort("date")}
                    >
                      <span>Data ord.</span>
                      <span className="supord-th-arrow">
                        {sortArrow("date")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-rit">
                    <span className="supord-th-plain">Rit</span>
                  </th>

                  <th className="supord-col-cons">
                    <span className="supord-th-plain">Cons</span>
                  </th>

                  <th className="supord-col-id">
                    <button
                      type="button"
                      className="supord-th-btn supord-th-btn--right"
                      onClick={() => toggleSort("id")}
                    >
                      <span>Ord.</span>
                      <span className="supord-th-arrow">{sortArrow("id")}</span>
                    </button>
                  </th>

                  <th className="supord-col-cliente">
                    <button
                      type="button"
                      className="supord-th-btn"
                      onClick={() => toggleSort("cliente")}
                    >
                      <span>Cliente</span>
                      <span className="supord-th-arrow">
                        {sortArrow("cliente")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-citta">
                    <button
                      type="button"
                      className="supord-th-btn"
                      onClick={() => toggleSort("citta")}
                    >
                      <span>Città</span>
                      <span className="supord-th-arrow">
                        {sortArrow("citta")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-total">
                    <button
                      type="button"
                      className="supord-th-btn supord-th-btn--right"
                      onClick={() => toggleSort("total")}
                    >
                      <span>Totale</span>
                      <span className="supord-th-arrow">
                        {sortArrow("total")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-art">
                    <button
                      type="button"
                      className="supord-th-btn supord-th-btn--right"
                      onClick={() => toggleSort("merch")}
                    >
                      <span>Articoli</span>
                      <span className="supord-th-arrow">
                        {sortArrow("merch")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-rosa">
                    <button
                      type="button"
                      className="supord-th-btn supord-th-btn--right"
                      onClick={() => toggleSort("rosa")}
                    >
                      <span>Rosa</span>
                      <span className="supord-th-arrow">
                        {sortArrow("rosa")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-fee">
                    <button
                      type="button"
                      className="supord-th-btn supord-th-btn--right"
                      onClick={() => toggleSort("fee")}
                    >
                      <span>Trasporto</span>
                      <span className="supord-th-arrow">
                        {sortArrow("fee")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-sconto">
                    <button
                      type="button"
                      className="supord-th-btn supord-th-btn--right"
                      onClick={() => toggleSort("sconto")}
                    >
                      <span>Sconto</span>
                      <span className="supord-th-arrow">
                        {sortArrow("sconto")}
                      </span>
                    </button>
                  </th>

                  <th className="supord-col-status">
                    <button
                      type="button"
                      className="supord-th-btn"
                      onClick={() => toggleSort("status")}
                    >
                      <span>Stato</span>
                      <span className="supord-th-arrow">
                        {sortArrow("status")}
                      </span>
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading &&
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={`skel-${i}`} className="supord-row-skel">
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar supord-skel-bar--short" /></td>
                      <td><span className="supord-skel-bar supord-skel-bar--wide" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar" /></td>
                      <td><span className="supord-skel-bar supord-skel-bar--wide" /></td>
                    </tr>
                  ))}

                {!loading && sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="supord-empty">
                      Nessun ordine trovato.
                    </td>
                  </tr>
                )}

                {!loading &&
                  sortedRows.length > 0 &&
                  sortedRows.map((o) => (
                    <tr key={o.id}>
                      <td className="supord-col-dateord">
                        {formatDateShort(o.created_at)}
                      </td>
                      <td className="supord-col-rit">
                        {formatDayMonthWithSlot(o.data_ritiro, o.ritiro_fascia)}
                      </td>
                      <td className="supord-col-cons">
                        {formatDayMonthWithSlot(
                          o.data_consegna,
                          o.consegna_fascia,
                        )}
                      </td>

                      <td className="supord-col-id">
                        <Link
                          className="supord-orderlink"
                          to={`/fornitore/ordini/${o.id}${fornitore
                              ? `?fornitore=${encodeURIComponent(fornitore)}`
                              : ""
                            }`}
                          title={`Apri ordine #${o.id}`}
                        >
                          <svg className="supord-eye-svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          <span className="supord-mono">{o.id}</span>
                        </Link>
                      </td>

                      <td className="supord-col-cliente">{o.cliente || "—"}</td>

                      <td className="supord-col-citta">
                        {formatCity(o.citta, o.provincia)}
                      </td>

                      <td className="supord-col-total">
                        {formatCurrency(o.total_cents)}
                      </td>

                      <td className="supord-col-art">
                        {formatCurrency(o.merch_total_cents ?? 0)}
                      </td>

                      <td className="supord-col-rosa">
                        {o.rosa_total_cents && o.rosa_total_cents > 0
                          ? formatCurrency(o.rosa_total_cents)
                          : "—"}
                      </td>

                      <td className="supord-col-fee">
                        {o.logistics_fee
                          ? formatCurrency(o.logistics_fee)
                          : "—"}
                      </td>

                      <td className="supord-col-sconto">
                        {o.sconto_cents > 0
                          ? `−${formatCurrency(o.sconto_cents)}`
                          : "—"}
                      </td>

                      <td className="supord-col-status">
                        <select
                          className="supord-inrow-select"
                          data-status={o.raw_status || ""}
                          value={o.raw_status || ""}
                          onChange={(e) => changeRowStatus(o, e.target.value)}
                          disabled={!!updating[o.id] || loading}
                        >
                          {DB_STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {meta.totalPages > 1 && (
            <div className="supord-pagination">
              <button
                type="button"
                className="supord-btn supord-btn-ghost"
                onClick={() => setPage(Math.max(1, (meta.page || 1) - 1))}
                disabled={loading || (meta.page || 1) <= 1}
              >
                ← Prec.
              </button>
              <span className="supord-page-info">
                Pagina {meta.page} / {meta.totalPages}
              </span>
              <button
                type="button"
                className="supord-btn supord-btn-ghost"
                onClick={() =>
                  setPage(Math.min(meta.totalPages, (meta.page || 1) + 1))
                }
                disabled={loading || (meta.page || 1) >= meta.totalPages}
              >
                Succ. →
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}